import React, { useState } from 'react';
import { api } from '../api';
import './StakeholderSearch.css';

interface SearchResult {
  id: string;
  name: string;
  affiliation: string | null;
  title: string | null;
  category: string | null;
  profile_image: string | null;
}

interface StakeholderSearchProps {
  onClose: () => void;
  onStakeholderAdded: () => void;
}

const StakeholderSearch: React.FC<StakeholderSearchProps> = ({ onClose, onStakeholderAdded }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  // Link modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedStakeholder, setSelectedStakeholder] = useState<SearchResult | null>(null);
  const [linkData, setLinkData] = useState({
    interest_level: 3,
    influence_level: 3,
    relationship: 'neutral' as 'supportive' | 'neutral' | 'opposed' | 'unknown'
  });
  const [isLinking, setIsLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (searchQuery.trim().length < 3) {
      setSearchError('Please enter at least 3 characters');
      return;
    }

    try {
      setIsSearching(true);
      setSearchError(null);
      const response = await api.searchStakeholderDirectory(searchQuery.trim());
      setSearchResults(response.results);
      
      if (response.results.length === 0) {
        setSearchError('No stakeholders found matching your search');
      }
    } catch (err: any) {
      setSearchError(err.message || 'Failed to search stakeholder directory');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectStakeholder = (stakeholder: SearchResult) => {
    setSelectedStakeholder(stakeholder);
    setLinkData({
      interest_level: 3,
      influence_level: 3,
      relationship: 'neutral'
    });
    setLinkError(null);
    setShowLinkModal(true);
  };

  const handleCancelLink = () => {
    setShowLinkModal(false);
    setSelectedStakeholder(null);
    setLinkError(null);
  };

  const handleConfirmLink = async () => {
    if (!selectedStakeholder) return;

    try {
      setIsLinking(true);
      setLinkError(null);
      
      await api.linkStakeholder(selectedStakeholder.id, linkData);
      
      // Success! Close both modals and refresh
      setShowLinkModal(false);
      onClose();
      onStakeholderAdded();
    } catch (err: any) {
      setLinkError(err.message || 'Failed to add stakeholder');
    } finally {
      setIsLinking(false);
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <>
      {/* Search Modal */}
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content stakeholder-search-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Add from Directory</h2>
            <button className="close-btn" onClick={onClose}>&times;</button>
          </div>

          <div className="modal-body">
            <div className="search-input-group">
              <input
                type="text"
                placeholder="Search by name or affiliation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                disabled={isSearching}
              />
              <button 
                onClick={handleSearch}
                disabled={isSearching || searchQuery.trim().length < 3}
                className="search-btn"
              >
                {isSearching ? 'Searching...' : 'Search'}
              </button>
            </div>

            {searchError && (
              <div className="search-error">{searchError}</div>
            )}

            {searchResults.length > 0 && (
              <div className="search-results">
                <p className="results-count">{searchResults.length} stakeholder(s) found</p>
                <div className="results-grid">
                  {searchResults.map(stakeholder => (
                    <div key={stakeholder.id} className="result-card">
                      <div className="result-avatar">
                        {stakeholder.profile_image ? (
                          <img src={stakeholder.profile_image} alt={stakeholder.name} />
                        ) : (
                          <div className="avatar-placeholder">{getInitials(stakeholder.name)}</div>
                        )}
                      </div>
                      <div className="result-info">
                        <h4>{stakeholder.name}</h4>
                        {stakeholder.affiliation && <p className="affiliation">{stakeholder.affiliation}</p>}
                        {stakeholder.title && <p className="title">{stakeholder.title}</p>}
                        {stakeholder.category && <span className="category-badge">{stakeholder.category}</span>}
                      </div>
                      <button 
                        className="add-btn"
                        onClick={() => handleSelectStakeholder(stakeholder)}
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Link Confirmation Modal */}
      {showLinkModal && selectedStakeholder && (
        <div className="modal-overlay" onClick={handleCancelLink}>
          <div className="modal-content link-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add {selectedStakeholder.name}</h2>
              <button className="close-btn" onClick={handleCancelLink}>&times;</button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                Please set the initial relationship parameters for this stakeholder.
              </p>

              {/* Interest Level */}
              <div className="form-group">
                <label>Interest Level *</label>
                <div className="radio-group">
                  {[1, 2, 3, 4, 5].map(level => (
                    <label key={level} className="radio-option">
                      <input
                        type="radio"
                        name="interest"
                        value={level}
                        checked={linkData.interest_level === level}
                        onChange={() => setLinkData({...linkData, interest_level: level})}
                        disabled={isLinking}
                      />
                      <span>{level}</span>
                    </label>
                  ))}
                </div>
                <div className="level-labels">
                  <span>Very Low</span>
                  <span>Low</span>
                  <span>Medium</span>
                  <span>High</span>
                  <span>Very High</span>
                </div>
              </div>

              {/* Influence Level */}
              <div className="form-group">
                <label>Influence Level *</label>
                <div className="radio-group">
                  {[1, 2, 3, 4, 5].map(level => (
                    <label key={level} className="radio-option">
                      <input
                        type="radio"
                        name="influence"
                        value={level}
                        checked={linkData.influence_level === level}
                        onChange={() => setLinkData({...linkData, influence_level: level})}
                        disabled={isLinking}
                      />
                      <span>{level}</span>
                    </label>
                  ))}
                </div>
                <div className="level-labels">
                  <span>Very Low</span>
                  <span>Low</span>
                  <span>Medium</span>
                  <span>High</span>
                  <span>Very High</span>
                </div>
              </div>

              {/* Relationship */}
              <div className="form-group">
                <label>Relationship *</label>
                <select
                  value={linkData.relationship}
                  onChange={(e) => setLinkData({...linkData, relationship: e.target.value as any})}
                  disabled={isLinking}
                >
                  <option value="supportive">Supportive</option>
                  <option value="neutral">Neutral</option>
                  <option value="opposed">Opposed</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>

              {linkError && (
                <div className="link-error">{linkError}</div>
              )}

              <div className="form-actions">
                <button 
                  onClick={handleCancelLink}
                  className="btn-cancel"
                  disabled={isLinking}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleConfirmLink}
                  className="btn-save"
                  disabled={isLinking}
                >
                  {isLinking ? 'Adding...' : 'Add Stakeholder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StakeholderSearch;
